import styled, { css } from 'styled-components';
import { useCallback, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { PatternFormat } from 'react-number-format';

import { config } from '_config';
import { ICONS_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { FIELDS_KEYS } from '_constants/forms';

import { sendMessage } from '_redux/contactUs/slice';
import {
  selectResult,
  selectError,
  selectLoading,
} from '_redux/contactUs/selector';

import TrustPilotSection from '_components/composed/sections/TrustPilotSection';
import AboutBannerSection from '_components/composed/sections/AboutBannerSection';
import ContactUsForm from '_components/composed/forms/ContactUsForm';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import { getHostname } from '_helpers/common';

const { colors, devices, fontSizes, spaces } = theme;
const { publicRuntimeConfig } = getConfig();

const StyledContactUsPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(
  forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(
  forwardRef(({ top, bottom, left, right, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const ItemGroup = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  gap: ${spaces.large4X}px;

  @media ${devices.tablet} {
    flex-direction: column;
    gap: 10px;
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: ${spaces.regular}px;

  @media ${devices.tablet} {
    gap: 10px;
  }
`;

const RightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeaderWrapper = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.regular}px;
  line-height: 130%;
  color: ${colors.gray[600]};

  @media ${devices.tablet} {
    font-size: ${fontSizes.small}px;
  }
`;

const LinkWrapper = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large}px;
  line-height: 150%;
  color: ${colors.blue[500]};

  a {
    text-decoration: none;
    color: ${colors.blue[500]};
  }

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
  }
`;

const RoundWrapper = styled.div`
  background: #d1e9ff;
  border-radius: 50px;
  height: 56px;
  width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${devices.tablet} {
    height: 52px;
    width: 52px;
  }
`;

const items = [
  {
    header: 'CALL',
    link: (
      <a href={`tel:${config.phone}`}>
        <PatternFormat
          value={config.phone?.substring(2)}
          displayType="text"
          format="(###) ###-####"
        />
      </a>
    ),
    icon: ICONS_ALIASES.callCalling,
  },
  {
    header: 'EMAIL',
    link: (
      <a
        href={`mailto:${config.email}?subject=Mail from Contact Us Trademark Site`}
      >
        support@trademark-genius.com
      </a>
    ),
    icon: ICONS_ALIASES.sms,
  },
  {
    header: 'HOUR',
    link: 'Monday - Friday: 9am - 5pm PST',
    icon: ICONS_ALIASES.clock,
  },
];

const ContactUsPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const result = useSelector(selectResult);
  const error = useSelector(selectError);
  const loading = useSelector(selectLoading);

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(
          sendMessage({
            name: values[FIELDS_KEYS.fullName],
            email: values[FIELDS_KEYS.email],
            phone: values[FIELDS_KEYS.phone],
            domain: getHostname(),
            subject: values[FIELDS_KEYS.subject],
            message: values[FIELDS_KEYS.message],
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <StyledContactUsPage>
      <AboutBannerSection
        header="Contact us"
        imgSrc={`${config.basePath}/images/contactus.svg`}
        subHeader={
          <ItemGroup>
            {items.map((item, idx) => {
              const { header, link, icon } = item;
              return (
                <Item key={`item:${idx}`}>
                  <RoundWrapper>
                    <Icon
                      alias={icon}
                      color={colors.blue[500]}
                      width={24}
                      height={24}
                    />
                  </RoundWrapper>
                  <RightWrapper>
                    <HeaderWrapper>{header}</HeaderWrapper>
                    <LinkWrapper>{link}</LinkWrapper>
                  </RightWrapper>
                </Item>
              );
            })}
          </ItemGroup>
        }
      />
      <ContactUsForm
        error={error}
        success={result}
        submitDisabled={loading}
        onSubmit={handleSubmit}
      />
      <TrustPilotSection
        title="Customer Reviews & Testimonials"
        subTitle="See why others are choosing Trademark Genius!"
      />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.blue[500]} />
            }
            color={colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistration)}
            textColor={colors.blue[500]}
            hoverColor={colors.white}
            hoverTextColor={colors.blue[500]}
          >
            Start Registering Now
          </Button>
        }
        imgSrc={`${config.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle size={686} rotate={-90} left={-63} top={-199} />
      <StyledImageContainer left={-44} top={285}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle
        size={686}
        rotate={-90}
        left={-232}
        top={'calc(40% - 30px)'}
      />
      <StyledImageContainer left={65} top={'calc(53% + 20px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={686} rotate={90} right={-199} top={155} />
      <StyledImageContainer right={-66} top={78}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledContactUsPage>
  );
};

export default ContactUsPage;
